import { ReportBatchValidationMessage } from 'constants/reports';
import { isDayLockedForUser } from 'redux/modules/dayProperties';
import { getDayProperties } from 'redux/modules/dayProperties.selectors';
import { getHourStates } from 'redux/modules/hourStates';
import { getTags } from 'redux/modules/tags.selectors';
import { createSelector } from 'reselect';
import { HourLockedReasons } from 'types/hour';
import type { Tag } from 'types/tag';
import { getCommonTagsForSelection } from 'utils/superreports';
import { notNull } from 'utils/typesafe';
import { getCurrentAccount } from './accounts';
import { getClients } from './clients';
import { getHourEntities } from './hours';
import {
  getAllLoadedProjects,
  getAllProjectsHaveBeenFetched,
  getProjectVariantMap,
  getProjectXhrStatus,
} from './projects';
import { getReportSelection, getXhrStatusUiState } from './superreports';
import { getUser, getUserPermissions } from './user';

const getTagsForSelection = createSelector(
  [getTags, getHourEntities, getProjectVariantMap, getReportSelection],
  (tags, hourEntities, projectMap, selection) => {
    const { hourIds } = selection;
    if (hourIds.length === 0 || !hourEntities) {
      return tags;
    }
    const projectIds = hourIds.map(id => hourEntities[id]?.project).filter(notNull);
    return getCommonTagsForSelection(tags, projectIds, projectMap);
  }
);

export const getBatchSelectionMetadata = createSelector(
  [getReportSelection, getHourEntities, getDayProperties],
  (selection, hourEntities, dayProperties) => {
    let lockedHourCount = 0;
    let billedHourCount = 0;
    let readonlyHoursCount = 0;
    let hasBillableHours = false;
    let hasNonBillableHours = false;
    let hasPlannedTime = false;
    let hasLoggedTime = false;
    let hasDrafts = false;
    let hasHoursOnLockedDays = false;

    const selectedUserIds = selection.hourIds.reduce((selectedUserIds, hourId) => {
      const hour = hourEntities[hourId];
      if (hour) {
        if (hour.locked && hour.locked_reason === HourLockedReasons.Locked) lockedHourCount++;
        if (hour.billed) billedHourCount++;
        if (!hour.manage) readonlyHoursCount++;
        hasBillableHours ||= hour.billable;
        hasNonBillableHours ||= !hour.billable;
        hasPlannedTime ||= !!hour.estimated_duration.total_minutes;
        hasLoggedTime ||= !!hour.duration.total_minutes;
        hasDrafts ||= !!hour.draft;
        selectedUserIds.add(hour.user.id);
        if (isDayLockedForUser(hour.user.id, dayProperties[hour.day])) {
          hasHoursOnLockedDays = true;
        }
      }
      return selectedUserIds;
    }, new Set<number>());

    return {
      allHoursArePlanned: hasPlannedTime && !hasLoggedTime && !hasDrafts,
      allHoursAreLocked: lockedHourCount === selection.hourIds.length,
      hasLockedHours: lockedHourCount > 0,
      allHoursAreBilled: billedHourCount === selection.hourIds.length,
      allHoursAreReadonly: readonlyHoursCount === selection.hourIds.length,
      hasReadonlyHours: readonlyHoursCount > 0,
      hasBilledHours: billedHourCount > 0,
      hasBillableHours,
      hasNonBillableHours,
      hasHoursOnLockedDays,
      selectedUserIds: Array.from(selectedUserIds),
    };
  }
);

const getCanUpdateProject = createSelector(
  [getBatchSelectionMetadata, getReportSelection, getUserPermissions],
  (
    { hasLockedHours, selectedUserIds, hasHoursOnLockedDays },
    { labelIds },
    userPermissions
  ): { canUpdateProject: boolean; cannotUpdateProjectReason?: string } => {
    if (hasHoursOnLockedDays) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason: ReportBatchValidationMessage.CannotUpdateLockedDay,
      };
    }

    if (hasLockedHours) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason: ReportBatchValidationMessage.CannotUpdateLocked,
      };
    }

    // Only restrict multi-user project changes for users without project:update permission
    if (selectedUserIds.length > 1 && !userPermissions.canUpdate.projects) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason:
          'You may not change the project of entries from multiple users at the same time.',
      };
    }

    if (labelIds.length > 1) {
      return {
        canUpdateProject: false,
        cannotUpdateProjectReason:
          'You may not make changes to both project and tags at the same time.',
      };
    }

    return { canUpdateProject: true };
  }
);

const getCanUpdateTags = createSelector(
  [getBatchSelectionMetadata, getReportSelection, getTagsForSelection],
  (
    { hasLockedHours, hasHoursOnLockedDays },
    { projectId },
    tags
  ): {
    canUpdateTags: boolean;
    cannotUpdateTagsReason?: string;
    tags: Array<Tag>;
  } => {
    if (hasHoursOnLockedDays) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason: ReportBatchValidationMessage.CannotUpdateLockedDay,
        tags,
      };
    }

    if (hasLockedHours) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason: ReportBatchValidationMessage.CannotUpdateLocked,
        tags,
      };
    }

    if (!tags.length) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason:
          'You may not make changes to entries from projects with no tags in common.',
        tags,
      };
    }

    if (projectId) {
      return {
        canUpdateTags: false,
        cannotUpdateTagsReason:
          'You may not make changes to both project and tags at the same time.',
        tags,
      };
    }

    return { canUpdateTags: true, tags };
  }
);

export const getCanUpdateState = createSelector(
  [getBatchSelectionMetadata],
  ({
    hasHoursOnLockedDays,
  }): {
    canUpdateState: boolean;
    cannotUpdateStateReason?: string;
  } => {
    return {
      canUpdateState: !hasHoursOnLockedDays,
      cannotUpdateStateReason: hasHoursOnLockedDays
        ? ReportBatchValidationMessage.CannotUpdateLockedDay
        : undefined,
    };
  }
);

export const getReportBatchActionsData = createSelector(
  [
    getCurrentAccount,
    getReportSelection,
    getUser,
    getXhrStatusUiState,
    getBatchSelectionMetadata,
    getClients,
    getAllLoadedProjects,
    getAllProjectsHaveBeenFetched,
    getProjectXhrStatus,
    getCanUpdateProject,
    getCanUpdateTags,
    getCanUpdateState,
    getUserPermissions,
    getHourStates,
  ],
  (
    account,
    selection,
    user,
    xhrStatus,
    {
      selectedUserIds,
      hasBillableHours,
      hasNonBillableHours,
      allHoursArePlanned,
      allHoursAreReadonly,
      hasReadonlyHours,
      hasLockedHours,
      allHoursAreLocked,
      hasBilledHours,
      allHoursAreBilled,
    },
    clients,
    projects,
    allProjectsLoaded,
    { isLoading: isLoadingProjects },
    { canUpdateProject, cannotUpdateProjectReason },
    { canUpdateTags, cannotUpdateTagsReason, tags },
    { canUpdateState, cannotUpdateStateReason },
    userPermissions,
    states
  ) => {
    const { isSaving } = xhrStatus;
    const isDeleting = isSaving;
    const numSelectedHours = selection.hourIds.length;
    const validUpdateBatchAction =
      selection.billedState !== null ||
      (selection.labelIds.length > 0 && canUpdateTags) ||
      (selection.projectId !== null && canUpdateProject) ||
      selection.lockedState !== null ||
      typeof selection.stateId !== 'undefined';

    const countedEntryLabel = numSelectedHours === 1 ? 'entry' : 'entries';
    const affectedEntriesLabel = `${numSelectedHours} ${countedEntryLabel} `;
    const deleteConfirmMessage = `This will permanently delete ${numSelectedHours} ${countedEntryLabel} from your Timely workspace, not just this report.`;

    const selectedProject = selection.projectId
      ? projects.find(project => project.base.id === selection.projectId)
      : null;

    const selectedProjectBillableState = selectedProject?.fullProject?.billable;

    // `fullProject.users` is only populated once the project has been fetched
    // singularly by id (see the Project type). Until it loads, membership is
    // unknown — treat it as such rather than flagging every selected user as a
    // non-member (which would pop the "add to project" prompt for real members).
    const projectMembers = selectedProject?.fullProject?.users;
    const projectMembershipLoaded = !!projectMembers;
    // Set for O(1) lookup instead of O(n) array.includes()
    const projectUserIds = new Set(projectMembers?.map(u => u.user_id) ?? []);
    const usersNotInProject =
      canUpdateProject && projectMembershipLoaded
        ? selectedUserIds.filter(userId => !projectUserIds.has(userId))
        : [];

    const [userId] = selectedUserIds;
    const allProjectsLoggingEnabled = !!account.features.all_projects_logging;
    const authorNotInSelectedProject =
      canUpdateProject && projectMembershipLoaded && !projectUserIds.has(userId);

    // Single-user: respect ALL_PROJECTS_LOGGING feature flag.
    //   - Feature ON:  don't block — prompt join/log-once instead (promptProjectJoin).
    //   - Feature OFF: block with the legacy userIsNotInProject message.
    // Multi-user: always use the usersNotInProject popover prompt (feature flag doesn't apply,
    //   button is not disabled — user picks add/skip from the prompt).
    const isSingleUser = selectedUserIds.length === 1;

    // Single-user legacy block (feature off): author not in project → hard block.
    const singleUserLegacyBlock =
      isSingleUser && !allProjectsLoggingEnabled && authorNotInSelectedProject;

    const userIsNotInProject = isSingleUser ? singleUserLegacyBlock : usersNotInProject.length > 0;

    // For single-user with ALL_PROJECTS_LOGGING feature: prompt how to log
    // (join / log once) instead of blocking. Only applies when single user.
    const promptProjectJoin =
      allProjectsLoggingEnabled && isSingleUser && authorNotInSelectedProject;

    // Single-user legacy block disables the Update button (master behaviour).
    // Multi-user userIsNotInProject shows a popover prompt instead — button stays enabled.
    const updateActionDisabled =
      isSaving ||
      isDeleting ||
      !validUpdateBatchAction ||
      isLoadingProjects ||
      singleUserLegacyBlock;

    const hasProjectBillableMismatch =
      !!selectedProject &&
      ((selectedProjectBillableState && hasNonBillableHours) ||
        (!selectedProjectBillableState && hasBillableHours));

    // Check if target project has required tags
    const selectedProjectHasRequiredTags =
      !!selectedProject?.fullProject?.required_labels ||
      (selectedProject?.fullProject?.required_label_ids?.length ?? 0) > 0;

    const showBatchLogPlannedTime = allHoursArePlanned && !!account.features.batch_log_planned_time;

    return {
      clients,
      currency: account.currency,
      projects,
      tags,
      selection,
      userId: selectedUserIds[0],
      selectedUserIds,
      states,
      selectedProject,
      ui: {
        isVisible: numSelectedHours > 0,
        isBillingDisabled: !account.features.billing,
        isUserAdmin: user.admin,
        canUpdateTags,
        cannotUpdateTagsReason,
        canUpdateState,
        cannotUpdateStateReason,
        updateLabel: isSaving ? 'Updating...' : 'Update',
        deleteLabel: isDeleting ? 'Deleting...' : 'Delete',
        deleteConfirmMessage,
        updateActionDisabled,
        canUpdateLocked: userPermissions.canUpdate.projects,
        deleteActionDisabled: hasLockedHours || isSaving || isDeleting,
        cannotDeleteReason: hasLockedHours ? 'You may not delete locked entries.' : undefined,
        affectedEntriesLabel,
        canUpdateProject,
        cannotUpdateProjectReason,
        allProjectsLoaded,
        isLoadingProjects,
        userIsNotInProject,
        usersNotInProject,
        promptProjectJoin,
        selectedProjectName: selectedProject?.fullProject?.name ?? selectedProject?.base?.name,
        hasProjectBillableMismatch,
        selectedProjectBillableState,
        selectedProjectHasRequiredTags,
        showBatchLogPlannedTime,
        hasLockedHours,
        allHoursAreLocked,
        hasBilledHours,
        allHoursAreBilled,
        allHoursAreReadonly,
        hasReadonlyHours,
        hasHourStatesFeature: !!account.features.hour_states,
        userRoleId: user.role_id,
      },
    };
  }
);
